import { assign, setup } from 'xstate';

export type TripArrayFieldContext = {
  /** Trip-leg synced values; address fields may hold Google Places objects from AddressInput. */
  values: unknown[];
  focusedIndex: number | null;
};

export type TripArrayFieldEvent =
  | { type: 'CHANGE'; index: number; value: unknown }
  | { type: 'RESIZE'; count: number }
  | { type: 'RESET' }
  | { type: 'FOCUS'; index: number }
  | { type: 'BLUR'; index: number };

function resizeValues(prev: unknown[], count: number): unknown[] {
  const c = Math.max(1, count);
  const next = prev.slice(0, c);
  while (next.length < c) next.push('');
  return next;
}

export function createTripArrayFieldMachine(id: string) {
  return setup({
    types: {
      context: {} as TripArrayFieldContext,
      events: {} as TripArrayFieldEvent,
    },
  }).createMachine({
    id,
    context: { values: [''], focusedIndex: null },
    initial: 'idle',
    states: {
      idle: {
        on: {
          FOCUS: {
            target: 'focused',
            actions: assign({
              focusedIndex: ({ event }) =>
                event.type === 'FOCUS' ? event.index : null,
            }),
          },
          CHANGE: {
            actions: assign({
              values: ({ context, event }) => {
                if (event.type !== 'CHANGE') return context.values;
                const next = [...context.values];
                while (next.length <= event.index) next.push('');
                next[event.index] = event.value;
                return next;
              },
            }),
          },
          RESIZE: {
            actions: assign({
              values: ({ context, event }) =>
                event.type === 'RESIZE'
                  ? resizeValues(context.values, event.count)
                  : context.values,
            }),
          },
        },
      },
      focused: {
        on: {
          CHANGE: {
            actions: assign({
              values: ({ context, event }) => {
                if (event.type !== 'CHANGE') return context.values;
                const next = [...context.values];
                while (next.length <= event.index) next.push('');
                next[event.index] = event.value;
                return next;
              },
            }),
          },
          RESIZE: {
            actions: assign({
              values: ({ context, event }) =>
                event.type === 'RESIZE'
                  ? resizeValues(context.values, event.count)
                  : context.values,
            }),
          },
          BLUR: {
            target: 'idle',
            actions: assign({
              focusedIndex: ({ context, event }) =>
                event.type === 'BLUR' && context.focusedIndex === event.index
                  ? null
                  : context.focusedIndex,
            }),
          },
          FOCUS: {
            actions: assign({
              focusedIndex: ({ event }) =>
                event.type === 'FOCUS' ? event.index : null,
            }),
          },
        },
      },
    },
    on: {
      RESET: {
        target: '.idle',
        actions: assign({ values: [''], focusedIndex: null }),
      },
    },
  });
}
